import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatEUR, DEFECT_STATUS, CLAIM_FAMILIES, WARRANTY_FAMILIES, WARRANTY_FAMILY_KEYS } from "@/lib/dict";
import { AlertTriangle, ShieldAlert, FileBadge, Wallet, TrendingUp, Activity, ArrowUpRight, AlertCircle, Building2, Settings, FileSpreadsheet, Download } from "lucide-react";
import { Link } from "react-router-dom";
import TimelineStats from "@/components/TimelineStats";
import CompareStats from "@/components/CompareStats";
import DashboardCustomizer, { AVAILABLE_WIDGETS } from "@/components/DashboardCustomizer";
import { toast } from "sonner";

function KpiCard({ icon: Icon, label, value, sub, color = "#023E73", testid }) {
  return (
    <Card data-testid={testid} className="p-5 hover:shadow-md transition-all border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-500">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}15`, color }}>
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const { data: kpis } = useQuery({ queryKey: ["kpis"], queryFn: () => api.get("/dashboard/kpis").then((r) => r.data) });
  const { data: activity = [] } = useQuery({ queryKey: ["activity"], queryFn: () => api.get("/dashboard/activity").then((r) => r.data) });
  const { data: actions = [] } = useQuery({ queryKey: ["actions"], queryFn: () => api.get("/dashboard/priority-actions").then((r) => r.data) });
  const { data: prefs } = useQuery({ queryKey: ["prefs"], queryFn: () => api.get("/user-preferences").then((r) => r.data) });

  const [widgets, setWidgets] = useState(AVAILABLE_WIDGETS.map((w) => ({ ...w, visible: true })));
  const [customOpen, setCustomOpen] = useState(false);
  const [comparePeriod, setComparePeriod] = useState("month");

  // Hydrate widgets from server prefs (once)
  useEffect(() => {
    if (prefs?.dashboard_widgets && Array.isArray(prefs.dashboard_widgets)) {
      // Merge with AVAILABLE_WIDGETS so newly added widgets are visible by default
      const saved = new Map(prefs.dashboard_widgets.map((w) => [w.id, w]));
      const merged = prefs.dashboard_widgets
        .filter((w) => AVAILABLE_WIDGETS.find((a) => a.id === w.id))
        .map((w) => ({ ...AVAILABLE_WIDGETS.find((a) => a.id === w.id), ...w }));
      AVAILABLE_WIDGETS.forEach((a) => { if (!saved.has(a.id)) merged.push({ ...a, visible: true }); });
      setWidgets(merged);
    }
  }, [prefs]);

  const savePrefs = useMutation({
    mutationFn: (dashboard_widgets) => api.patch("/user-preferences", { dashboard_widgets }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prefs"] }),
  });

  const updateWidgets = (next) => { setWidgets(next); savePrefs.mutate(next); };

  const visible = useMemo(() => new Set(widgets.filter((w) => w.visible).map((w) => w.id)), [widgets]);
  const orderedIds = useMemo(() => widgets.filter((w) => w.visible).map((w) => w.id), [widgets]);

  const downloadXlsx = async () => {
    try {
      const token = localStorage.getItem("wrm_token");
      const resp = await fetch(`${API}/exports/xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `warranty-risk-export.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (e) { toast.error("Échec de l'export"); }
  };

  if (!kpis) return <div className="text-slate-500">Chargement…</div>;

  const renderWidget = (id) => {
    if (id === "kpis") {
      return (
        <div key="kpis" className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard testid="kpi-open-defects" icon={AlertTriangle} label="Désordres ouverts" value={kpis.open_defects} color="#F59E0B" sub="à traiter" />
          <KpiCard testid="kpi-active-claims" icon={ShieldAlert} label="Sinistres actifs" value={kpis.active_claims} color="#EF4444" sub="DO + Multirisque" />
          <KpiCard testid="kpi-do-actions" icon={Activity} label="Actions DO" value={kpis.do_actions} color="#7F1D1D" sub="Dommages-Ouvrage" />
          <KpiCard testid="kpi-expiring-warranties" icon={FileBadge} label="Garanties J-90" value={kpis.expiring_warranties} color="#3B82F6" sub="à échéance" />
          <KpiCard testid="kpi-est-cost" icon={Wallet} label="Coût estimé" value={formatEUR(kpis.total_estimated_cost)} sub="cumul désordres" />
          <KpiCard testid="kpi-compensation" icon={TrendingUp} label="Indemnisation attendue" value={formatEUR(kpis.total_compensation)} color="#10B981" sub="sinistres" />
          <KpiCard testid="kpi-remaining" icon={ArrowUpRight} label="Reste à charge" value={formatEUR(kpis.total_remaining)} color="#EF4444" sub="exposition" />
          <KpiCard testid="kpi-actual-cost" icon={Wallet} label="Coût réel" value={formatEUR(kpis.total_actual_cost)} sub="dépensé" />
        </div>
      );
    }
    if (id === "priority" || id === "status") {
      // Render side-by-side when both visible, else full width
      if (id === "priority" && visible.has("status")) {
        return (
          <div key="priority-status" className="grid lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2 border-slate-200" data-testid="widget-priority">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display text-lg font-semibold text-slate-900">Actions prioritaires</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Échéances et relances à traiter en priorité</p>
                </div>
              </div>
              <div className="space-y-2">
                {actions.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Aucune action en attente</div>}
                {actions.map((a, idx) => (
                  <div key={idx} data-testid={`action-${idx}`} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-300 transition">
                    <div className={`mt-1 w-2 h-2 rounded-full ${a.severity === "high" ? "bg-red-500" : "bg-amber-500"}`} />
                    <div className="flex-1 text-sm text-slate-700">{a.label}</div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{a.type}</Badge>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="widget-status">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Désordres par statut</h2>
              <div className="space-y-2.5">
                {Object.entries(kpis.defects_by_status || {}).map(([k, v]) => {
                  const s = DEFECT_STATUS[k] || { label: k, color: "bg-slate-100 text-slate-700" };
                  const total = Object.values(kpis.defects_by_status).reduce((x, y) => x + y, 0);
                  const pct = total ? Math.round((v / total) * 100) : 0;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>
                        <span className="text-slate-500 font-semibold">{v}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#023E73" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        );
      }
      if (id === "status" && visible.has("priority")) return null; // already rendered above
    }
    if (id === "risk" || id === "activity") {
      if (id === "risk" && visible.has("activity")) {
        return (
          <div key="risk-activity" className="grid lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2 border-slate-200" data-testid="widget-risk">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Actifs à risque</h2>
              <div className="divide-y divide-slate-100">
                {(kpis.top_assets_by_risk || []).map((a) => (
                  <Link key={a.id} to={`/assets/${a.id}`} data-testid={`risk-asset-${a.id}`} className="flex items-center gap-4 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#023E7315", color: "#023E73" }}><Building2 size={16} /></div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.city} • {a.type}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Risk</div>
                      <div className={`font-display font-bold text-lg ${a.risk_score > 70 ? "text-red-600" : a.risk_score > 50 ? "text-amber-600" : "text-emerald-600"}`}>{a.risk_score}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="widget-activity">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Activité récente</h2>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {activity.slice(0, 12).map((a) => (
                  <div key={a.id} className="flex gap-3 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 bg-slate-300 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-700"><span className="font-semibold">{a.user_name || "Système"}</span> · {a.action} <span className="text-slate-500">({a.entity_type})</span></div>
                      <div className="text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                ))}
                {activity.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Aucune activité</div>}
              </div>
            </Card>
          </div>
        );
      }
      if (id === "activity" && visible.has("risk")) return null;
    }
    if (id === "compare") {
      return <CompareStats key="compare" period={comparePeriod} onPeriodChange={setComparePeriod} />;
    }
    if (id === "timeline") {
      return <TimelineStats key="timeline" />;
    }
    if (id === "warranty") {
      const counts = kpis.defects_by_warranty || {};
      const total = WARRANTY_FAMILY_KEYS.reduce((s, k) => s + (counts[k] || 0), 0);
      const costs = kpis.warranty_cost || {};
      return (
        <Card key="warranty" className="p-5 border-slate-200" data-testid="widget-warranty">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Familles de garantie</div>
              <h2 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Désordres par garantie</h2>
              <p className="text-xs text-slate-500 mt-0.5">Suivis séparés GPA, GBF, Décennale, DO et hors garantie</p>
            </div>
            <Link to="/defects" className="text-xs text-slate-500 hover:text-slate-900 underline">Voir les désordres →</Link>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            {WARRANTY_FAMILY_KEYS.map((k) => {
              const w = WARRANTY_FAMILIES[k];
              const v = counts[k] || 0;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return (
                <Link key={k} to={`/defects?warranty=${k}`} data-testid={`warranty-tile-${k}`} className="rounded-lg border border-slate-200 p-3 hover:shadow-sm hover:-translate-y-0.5 transition-all bg-white">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: w.color }} />
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{w.label}</div>
                  </div>
                  <div className="font-display text-2xl font-bold text-slate-900 mt-2" style={{ color: w.color }}>{v}</div>
                  <div className="text-[10px] text-slate-500">{pct}% • {formatEUR(costs[k] || 0)}</div>
                  <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-2">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: w.color }} />
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Cockpit</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1.5">Vue d&apos;ensemble opérationnelle de vos désordres, sinistres et garanties.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-xlsx" variant="outline" size="sm" onClick={downloadXlsx}><FileSpreadsheet size={14} className="mr-1.5" /> Export Excel</Button>
          <Button data-testid="customize-dashboard" variant="outline" size="sm" onClick={() => setCustomOpen(true)}><Settings size={14} className="mr-1.5" /> Personnaliser</Button>
        </div>
      </div>

      {orderedIds.map((id) => renderWidget(id))}

      <DashboardCustomizer open={customOpen} onClose={() => setCustomOpen(false)} value={widgets} onChange={updateWidgets} />
    </div>
  );
}
